import { create } from 'zustand';
import {
  tasksAPI,
  TaskItem,
  TaskWithSubtasks,
  TaskCreate,
  TaskUpdate,
  TaskStatus,
  TaskSummary,
} from '../services/api';

interface TaskState {
  tasks: TaskItem[];
  selectedTask: TaskWithSubtasks | null;
  summary: TaskSummary | null;
  selectedProjectId: string | null;
  statusFilter: string | null;
  searchQuery: string;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTasks: (params?: {
    project_id?: string;
    status?: string;
    priority?: string;
    parent_task_id?: string;
    search?: string;
  }) => Promise<void>;
  fetchTask: (taskId: string) => Promise<TaskWithSubtasks | null>;
  fetchSummary: (projectId: string) => Promise<void>;
  createTask: (data: TaskCreate) => Promise<TaskItem>;
  updateTask: (taskId: string, data: TaskUpdate) => Promise<TaskItem>;
  deleteTask: (taskId: string) => Promise<void>;
  updateStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  setSelectedProjectId: (projectId: string | null) => void;
  setStatusFilter: (status: string | null) => void;
  setSearchQuery: (query: string) => void;
  clearSelectedTask: () => void;
  clearError: () => void;
}

const getErrorMessage = (error: any, defaultMsg: string): string => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => `${e.loc?.slice(1).join('.')}: ${e.msg}`).join(', ') || defaultMsg;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return defaultMsg;
};

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  selectedTask: null,
  summary: null,
  selectedProjectId: null,
  statusFilter: null,
  searchQuery: '',
  isLoading: false,
  error: null,

  fetchTasks: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const { selectedProjectId, statusFilter, searchQuery } = get();
      const mergedParams = {
        project_id: selectedProjectId || undefined,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        parent_task_id: 'null', // Only top-level tasks
        ...params,
      };
      const tasks = await tasksAPI.list(mergedParams);
      set({ tasks, isLoading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to fetch tasks'), isLoading: false });
    }
  },

  fetchTask: async (taskId: string) => {
    set({ isLoading: true, error: null });
    try {
      const task = await tasksAPI.get(taskId);
      set({ selectedTask: task, isLoading: false });
      return task;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to fetch task'), isLoading: false });
      return null;
    }
  },

  fetchSummary: async (projectId: string) => {
    try {
      const summary = await tasksAPI.summary(projectId);
      set({ summary });
    } catch (error: any) {
      // Non-blocking
    }
  },

  createTask: async (data: TaskCreate) => {
    set({ isLoading: true, error: null });
    try {
      const task = await tasksAPI.create(data);
      // Refresh the task list
      await get().fetchTasks();
      if (data.project_id) {
        await get().fetchSummary(data.project_id);
      }
      set({ isLoading: false });
      return task;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to create task'), isLoading: false });
      throw error;
    }
  },

  updateTask: async (taskId: string, data: TaskUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const task = await tasksAPI.update(taskId, data);
      await get().fetchTasks();
      const { selectedProjectId } = get();
      if (selectedProjectId) {
        await get().fetchSummary(selectedProjectId);
      }
      set({ isLoading: false });
      return task;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to update task'), isLoading: false });
      throw error;
    }
  },

  deleteTask: async (taskId: string) => {
    set({ isLoading: true, error: null });
    try {
      await tasksAPI.delete(taskId);
      await get().fetchTasks();
      const { selectedProjectId } = get();
      if (selectedProjectId) {
        await get().fetchSummary(selectedProjectId);
      }
      set({ selectedTask: null, isLoading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to delete task'), isLoading: false });
      throw error;
    }
  },

  updateStatus: async (taskId: string, status: TaskStatus) => {
    try {
      await tasksAPI.updateStatus(taskId, status);
      await get().fetchTasks();
      const { selectedProjectId } = get();
      if (selectedProjectId) {
        await get().fetchSummary(selectedProjectId);
      }
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Failed to update status') });
    }
  },

  setSelectedProjectId: (projectId: string | null) => {
    set({ selectedProjectId: projectId });
    if (projectId) {
      get().fetchTasks({ project_id: projectId, parent_task_id: 'null' });
      get().fetchSummary(projectId);
    } else {
      get().fetchTasks({ parent_task_id: 'null' });
    }
  },

  setStatusFilter: (status: string | null) => {
    set({ statusFilter: status });
    get().fetchTasks();
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
    get().fetchTasks();
  },

  clearSelectedTask: () => set({ selectedTask: null }),
  clearError: () => set({ error: null }),
}));
